/**
 * Document Management System page (Phase 22).
 *
 * Single combined list of folders + files at the current level. Clicking a
 * folder drills in (the list reloads for that folder); clicking a file opens
 * it in the right-side viewer. Breadcrumbs handle navigation back up. Search
 * flips the list into whole-site mode (files only, since folders aren't
 * meaningful results when the user is looking for a specific file).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronRight,
  CornerLeftUp,
  File as FileGenericIcon,
  FileCode2,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderPlus,
  Home,
  Image as ImageIcon,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { useAppStore } from "../stores/appStore";
import {
  deleteDocument as apiDeleteDocument,
  deleteFolder as apiDeleteFolder,
  listDocuments as apiListDocuments,
  listFolders as apiListFolders,
  updateDocument as apiUpdateDocument,
  updateFolder as apiUpdateFolder,
  type DMSDocument,
  type DMSDocumentType,
  type DMSFolder,
} from "../lib/dmsApi";
import { FileViewer } from "../components/dms/FileViewer";
import { UploadDocumentModal } from "../components/dms/UploadDocumentModal";
import { CreateFolderModal } from "../components/dms/CreateFolderModal";
import { RenameModal } from "../components/dms/RenameModal";


// Combined row union: each list entry is either a folder (drill-in) or a
// document (opens in the viewer). Kept narrow so callers can switch on `kind`.
type ListEntry =
  | { kind: "folder"; folder: DMSFolder }
  | { kind: "document"; doc: DMSDocument };


export default function DocumentManagementSystem() {
  const { selectedSiteId } = useAppStore();

  // Folder list for the whole site (used for the parent-folder lookup +
  // search-mode breadcrumbs). We don't render a tree any more — just the
  // children of the current folder, mixed with files.
  const [folders, setFolders] = useState<DMSFolder[]>([]);
  const [documents, setDocuments] = useState<DMSDocument[]>([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Where in the tree we are. null = site root; otherwise the folder object.
  const [currentFolder, setCurrentFolder] = useState<DMSFolder | null>(null);

  const [search, setSearch] = useState("");

  // Viewer state — which document is showing on the right pane.
  const [viewerDoc, setViewerDoc] = useState<DMSDocument | null>(null);

  // Modals
  const [uploadOpen, setUploadOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [renameDoc, setRenameDoc] = useState<DMSDocument | null>(null);
  const [renameFolder, setRenameFolder] = useState<DMSFolder | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<
    | { kind: "document"; doc: DMSDocument }
    | { kind: "folder"; folder: DMSFolder; nonEmpty: boolean }
    | null
  >(null);

  // ── Loaders ─────────────────────────────────────────────────────

  const reloadTree = useCallback(async () => {
    if (!selectedSiteId) return;
    setLoadingTree(true);
    try {
      const data = await apiListFolders(selectedSiteId);
      setFolders(data);
      // If the folder we were sitting in got renamed/moved upstream, refresh
      // our copy so the breadcrumbs stay current.
      if (currentFolder) {
        const fresh = data.find((f) => f.id === currentFolder.id);
        if (fresh) setCurrentFolder(fresh);
        else setCurrentFolder(null);
      }
    } finally {
      setLoadingTree(false);
    }
  }, [selectedSiteId, currentFolder]);

  const reloadDocs = useCallback(async () => {
    if (!selectedSiteId) return;
    setLoadingDocs(true);
    try {
      if (search.trim()) {
        const { items } = await apiListDocuments({
          virtual_site_id: selectedSiteId,
          search: search.trim(),
          include_subfolders: true,
        });
        setDocuments(items);
      } else if (currentFolder === null) {
        const { items } = await apiListDocuments({
          virtual_site_id: selectedSiteId,
          folder_id: null,
        });
        setDocuments(items);
      } else {
        const { items } = await apiListDocuments({
          virtual_site_id: selectedSiteId,
          folder_id: currentFolder.id,
        });
        setDocuments(items);
      }
    } finally {
      setLoadingDocs(false);
    }
  }, [selectedSiteId, currentFolder, search]);

  useEffect(() => {
    reloadTree();
    // We deliberately don't add currentFolder here — reloadTree refreshes the
    // whole list including the current folder's metadata; including it in the
    // deps would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteId]);
  useEffect(() => {
    reloadDocs();
  }, [reloadDocs]);

  // Drop the viewer's doc if it disappears (deleted, moved out of view, etc.)
  useEffect(() => {
    if (viewerDoc && !documents.some((d) => d.id === viewerDoc.id) && !search) {
      setViewerDoc(null);
    }
  }, [documents, viewerDoc, search]);

  // ── Derived data ───────────────────────────────────────────────

  // Children of the current folder, sorted alphabetically. In search mode
  // we hide folders entirely — only files are relevant when the user is
  // trying to locate a specific document.
  const subfolders = useMemo(() => {
    if (search.trim()) return [];
    const parentId = currentFolder?.id ?? null;
    return folders
      .filter((f) => f.parent_folder_id === parentId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [folders, currentFolder, search]);

  // One combined list, folders first then files. Each item carries a `kind`
  // so the row renderer can dispatch on it.
  const entries: ListEntry[] = useMemo(() => {
    const folderRows: ListEntry[] = subfolders.map((f) => ({ kind: "folder", folder: f }));
    const docRows: ListEntry[] = documents.map((d) => ({ kind: "document", doc: d }));
    return [...folderRows, ...docRows];
  }, [subfolders, documents]);

  const currentVersion = useMemo(() => {
    if (!viewerDoc) return null;
    return (
      viewerDoc.versions.find((v) => v.is_current && v.status === "ready") ??
      viewerDoc.versions.find((v) => v.status === "ready") ??
      viewerDoc.versions[0] ??
      null
    );
  }, [viewerDoc]);

  // Breadcrumbs derived from the current folder's path. Each crumb is a
  // pointer back up the tree the user can click to jump levels.
  const breadcrumbs = useMemo(() => {
    if (!currentFolder) return [{ id: null as string | null, name: "Root" }];
    const parts = currentFolder.path.split("/").filter(Boolean);
    const items: { id: string | null; name: string }[] = [{ id: null, name: "Root" }];
    let cur = "";
    for (const p of parts) {
      cur = cur ? `${cur}/${p}` : p;
      const f = folders.find((x) => x.path === cur);
      items.push({ id: f?.id ?? null, name: p });
    }
    return items;
  }, [currentFolder, folders]);

  // ── Handlers ───────────────────────────────────────────────────

  const navigateToFolder = (folder: DMSFolder | null) => {
    setCurrentFolder(folder);
    setViewerDoc(null);
    setSearch("");
  };

  const handleRowClick = (entry: ListEntry) => {
    if (entry.kind === "folder") {
      navigateToFolder(entry.folder);
    } else {
      setViewerDoc(entry.doc);
    }
  };

  const handleRenameDoc = async (newName: string) => {
    if (!renameDoc) return;
    await apiUpdateDocument(renameDoc.id, { name: newName });
    await reloadDocs();
  };
  const handleRenameFolder = async (newName: string) => {
    if (!renameFolder) return;
    await apiUpdateFolder(renameFolder.id, { name: newName });
    await reloadTree();
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    if (confirmDelete.kind === "document") {
      await apiDeleteDocument(confirmDelete.doc.id);
      await reloadDocs();
    } else {
      await apiDeleteFolder(confirmDelete.folder.id, confirmDelete.nonEmpty);
      if (currentFolder?.id === confirmDelete.folder.id) {
        setCurrentFolder(null);
      }
      await reloadTree();
      await reloadDocs();
    }
    setConfirmDelete(null);
  };

  if (!selectedSiteId) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-surface-400">
        Select a virtual site to use the Document Management System.
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className="flex h-[calc(100vh-6rem)] lg:h-[calc(100vh-7rem)] flex-col gap-3">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-surface-50">Document Management System</h1>
          <p className="text-xs text-surface-400">
            Folders, files, and links to assets · instrumentation · maintenance · risk · 3D models
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-surface-500" />
            <Input
              className="pl-8 pr-8 w-64"
              placeholder="Search files across the site…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-surface-500 hover:text-surface-200"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setCreateFolderOpen(true)}
          >
            <FolderPlus className="h-4 w-4" />
            New folder
          </Button>
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            <Upload className="h-4 w-4" />
            Upload
          </Button>
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => {
              reloadTree();
              reloadDocs();
            }}
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 gap-3">
        {/* Main list — narrow when the viewer is open, full-width otherwise */}
        <main
          className={`flex min-w-0 flex-col rounded-xl border border-white/10 bg-surface-900/40 ${
            viewerDoc && currentVersion
              ? "lg:w-[340px] lg:shrink-0 flex-1 lg:flex-none"
              : "flex-1"
          }`}
        >
          {/* Breadcrumbs (or search-mode banner) */}
          <div className="flex flex-wrap items-center gap-1 border-b border-white/10 px-4 py-2 text-xs">
            {search ? (
              <span className="text-surface-300">
                Searching: <span className="text-surface-100 font-medium">"{search}"</span>{" "}
                <span className="text-surface-500">
                  ({entries.length} file{entries.length === 1 ? "" : "s"} found)
                </span>
              </span>
            ) : (
              breadcrumbs.map((crumb, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight className="h-3 w-3 text-surface-500" />}
                  <button
                    type="button"
                    className={
                      i === breadcrumbs.length - 1
                        ? "text-surface-100 font-medium"
                        : "text-surface-400 hover:text-surface-200"
                    }
                    onClick={() => {
                      if (crumb.id === null) navigateToFolder(null);
                      else {
                        const f = folders.find((x) => x.id === crumb.id);
                        if (f) navigateToFolder(f);
                      }
                    }}
                  >
                    {i === 0 ? (
                      <span className="inline-flex items-center gap-1">
                        <Home className="h-3 w-3" /> {crumb.name}
                      </span>
                    ) : (
                      crumb.name
                    )}
                  </button>
                </span>
              ))
            )}
          </div>

          {/* Combined list */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {(loadingTree || loadingDocs) && entries.length === 0 ? (
              <div className="flex items-center gap-2 p-6 text-sm text-surface-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <CombinedList
                entries={entries}
                currentFolder={currentFolder}
                searching={!!search.trim()}
                viewerDocId={viewerDoc?.id ?? null}
                onUpOneLevel={() => {
                  if (!currentFolder) return;
                  if (!currentFolder.parent_folder_id) {
                    navigateToFolder(null);
                    return;
                  }
                  const parent = folders.find((f) => f.id === currentFolder.parent_folder_id);
                  navigateToFolder(parent ?? null);
                }}
                onRowClick={handleRowClick}
                onRenameDoc={setRenameDoc}
                onRenameFolder={setRenameFolder}
                onDeleteDoc={(doc) => setConfirmDelete({ kind: "document", doc })}
                onDeleteFolder={(folder) =>
                  setConfirmDelete({
                    kind: "folder",
                    folder,
                    nonEmpty: folder.document_count > 0 || folder.subfolder_count > 0,
                  })
                }
              />
            )}
          </div>
        </main>

        {/* Viewer pane (lg+) — takes the rest of the row */}
        {viewerDoc && currentVersion && (
          <aside className="hidden lg:flex flex-col flex-1 min-w-0 rounded-xl border border-white/10 bg-surface-900/40">
            <FileViewer
              version={currentVersion}
              documentType={viewerDoc.document_type}
              flush
            />
          </aside>
        )}
      </div>

      {/* Mobile/medium viewer: full-screen overlay */}
      {viewerDoc && currentVersion && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 top-16 z-30 bg-surface-950/95 backdrop-blur p-3">
          <div className="flex h-full flex-col">
            <div className="flex justify-end pb-2">
              <Button size="sm" variant="ghost" onClick={() => setViewerDoc(null)}>
                <X className="h-4 w-4" /> Close
              </Button>
            </div>
            <div className="flex-1 min-h-0">
              <FileViewer
                version={currentVersion}
                documentType={viewerDoc.document_type}
                flush
              />
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <UploadDocumentModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        virtualSiteId={selectedSiteId}
        folder={currentFolder}
        onUploaded={() => {
          reloadTree();
          reloadDocs();
        }}
      />
      <CreateFolderModal
        isOpen={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        virtualSiteId={selectedSiteId}
        parent={currentFolder}
        onCreated={() => reloadTree()}
      />
      {renameDoc && (
        <RenameModal
          isOpen={true}
          onClose={() => setRenameDoc(null)}
          initialName={renameDoc.name}
          label="document"
          onSubmit={handleRenameDoc}
        />
      )}
      {renameFolder && (
        <RenameModal
          isOpen={true}
          onClose={() => setRenameFolder(null)}
          initialName={renameFolder.name}
          label="folder"
          onSubmit={handleRenameFolder}
        />
      )}
      <ConfirmDialog
        isOpen={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDeleteConfirmed}
        title={
          confirmDelete?.kind === "folder"
            ? confirmDelete.nonEmpty
              ? "Delete folder and contents?"
              : "Delete folder?"
            : "Delete document?"
        }
        message={
          confirmDelete?.kind === "folder"
            ? confirmDelete.nonEmpty
              ? `"${confirmDelete.folder.name}" contains files or subfolders. Everything inside will be deleted from S3 too.`
              : `Delete "${confirmDelete.folder.name}"?`
            : confirmDelete?.kind === "document"
              ? `Delete "${confirmDelete.doc.name}" and every uploaded version? This removes the files from S3.`
              : ""
        }
        confirmLabel="Delete"
      />
    </div>
  );
}


// ── Combined folders + files list ────────────────────────────────────

const TYPE_ICONS: Record<DMSDocumentType, typeof FileText> = {
  pdf: FileText,
  image: ImageIcon,
  csv: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  json: FileCode2,
  xml: FileCode2,
  text: FileText,
  docx: FileText,
  other: FileGenericIcon,
};

function CombinedList({
  entries, currentFolder, searching, viewerDocId,
  onUpOneLevel, onRowClick,
  onRenameDoc, onRenameFolder, onDeleteDoc, onDeleteFolder,
}: {
  entries: ListEntry[];
  currentFolder: DMSFolder | null;
  searching: boolean;
  viewerDocId: string | null;
  onUpOneLevel: () => void;
  onRowClick: (entry: ListEntry) => void;
  onRenameDoc: (doc: DMSDocument) => void;
  onRenameFolder: (folder: DMSFolder) => void;
  onDeleteDoc: (doc: DMSDocument) => void;
  onDeleteFolder: (folder: DMSFolder) => void;
}) {
  // No entries → friendly empty state. Two cases: empty folder vs no search
  // results. The wording differs so the user knows which.
  if (entries.length === 0 && !currentFolder && !searching) {
    return (
      <div className="p-8 text-center text-sm text-surface-400">
        No folders or files at the site root. Upload a file or create a folder to get started.
      </div>
    );
  }
  if (entries.length === 0 && searching) {
    return (
      <div className="p-8 text-center text-sm text-surface-400">
        No files match this search.
      </div>
    );
  }

  return (
    <ul className="divide-y divide-white/5">
      {/* ".." row — only when we're inside a folder and not searching */}
      {currentFolder && !searching && (
        <li
          className="group flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-white/[0.04]"
          onClick={onUpOneLevel}
        >
          <CornerLeftUp className="h-4 w-4 shrink-0 text-surface-400" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-surface-200">..</div>
            <div className="text-[11px] text-surface-500">Up one level</div>
          </div>
        </li>
      )}

      {entries.length === 0 && currentFolder && (
        <li className="px-4 py-8 text-center text-sm text-surface-400">
          This folder is empty.
        </li>
      )}

      {entries.map((entry) => {
        if (entry.kind === "folder") {
          return (
            <FolderRow
              key={`f-${entry.folder.id}`}
              folder={entry.folder}
              onClick={() => onRowClick(entry)}
              onRename={() => onRenameFolder(entry.folder)}
              onDelete={() => onDeleteFolder(entry.folder)}
            />
          );
        }
        return (
          <DocumentRow
            key={`d-${entry.doc.id}`}
            doc={entry.doc}
            isSelected={viewerDocId === entry.doc.id}
            onClick={() => onRowClick(entry)}
            onRename={() => onRenameDoc(entry.doc)}
            onDelete={() => onDeleteDoc(entry.doc)}
          />
        );
      })}
    </ul>
  );
}


function FolderRow({
  folder, onClick, onRename, onDelete,
}: {
  folder: DMSFolder;
  onClick: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  return (
    <li
      className="group flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-white/[0.04]"
      onClick={onClick}
    >
      <Folder className="h-5 w-5 shrink-0 text-amber-400" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-surface-100 flex items-center gap-1.5">
          {folder.name}
          {folder.is_system && (
            <Lock className="h-3 w-3 text-surface-500" aria-label="System folder" />
          )}
        </div>
        <div className="text-[11px] text-surface-500">
          Folder · {folder.document_count} file{folder.document_count === 1 ? "" : "s"}
          {folder.subfolder_count > 0 ? ` · ${folder.subfolder_count} subfolder${folder.subfolder_count === 1 ? "" : "s"}` : ""}
        </div>
      </div>
      {!folder.is_system && (
        <div className="hidden group-hover:flex items-center gap-1">
          <button
            type="button"
            title="Rename folder"
            className="text-surface-400 hover:text-surface-100 p-1"
            onClick={(e) => {
              e.stopPropagation();
              onRename();
            }}
          >
            ✎
          </button>
          <button
            type="button"
            title="Delete folder"
            className="text-surface-400 hover:text-red-300 p-1"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
      <ChevronRight className="h-4 w-4 text-surface-500 shrink-0" />
    </li>
  );
}


function DocumentRow({
  doc, isSelected, onClick, onRename, onDelete,
}: {
  doc: DMSDocument;
  isSelected: boolean;
  onClick: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const Icon = TYPE_ICONS[doc.document_type] ?? FileGenericIcon;
  const latest = doc.versions[0];
  return (
    <li
      className={`group flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-white/[0.04] ${
        isSelected ? "bg-brand-500/15" : ""
      }`}
      onClick={onClick}
    >
      <Icon className="h-5 w-5 shrink-0 text-surface-300" />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-surface-100 truncate">{doc.name}</div>
        <div className="text-[11px] text-surface-500 flex flex-wrap gap-x-2">
          <span>{doc.document_type.toUpperCase()}</span>
          {latest && <span>v{latest.version_number}</span>}
          {doc.folder_path && <span>{doc.folder_path}</span>}
          {doc.tags && <span>tags: {doc.tags}</span>}
          {doc.links.length > 0 && (
            <span>· {doc.links.length} link{doc.links.length === 1 ? "" : "s"}</span>
          )}
        </div>
      </div>
      <div className="hidden group-hover:flex items-center gap-1">
        <button
          type="button"
          title="Rename"
          className="text-surface-400 hover:text-surface-100 p-1"
          onClick={(e) => {
            e.stopPropagation();
            onRename();
          }}
        >
          ✎
        </button>
        <button
          type="button"
          title="Delete"
          className="text-surface-400 hover:text-red-300 p-1"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}
